import { useEffect, useState, type CSSProperties } from 'react';
import { useMsal } from '@azure/msal-react';
import { Capacitor } from '@capacitor/core';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { ProgressLoading } from '@/components/shared/loadings/progress';
import { BackgroundVideo } from '@/components/shared/video/background';
import { getImageUrl, cn } from '@/lib/core/utils';
import { AppContext, SplashContext } from '@/lib/core/types';
import { loginRequest } from '@/lib/auth/msalConfig';
import { launchNativeLogin } from '@/lib/auth/nativeImplicitAuth';
import { useLocaleStore } from '@/lib/core';
import {
  AppSplashScreenContentResponseModel,
  AppSplashScreenPropertiesModel,
  ApiBlockItemModel,
  IApiElementModel,
  SplashPinStepElementContentModelContentType,
  SplashOnboardingStep2ElementContentModelContentType,
  SplashWelcomeScreenStepElementContentModelContentType,
  SplashOnboardingStep1ElementContentModelContentType,
  SplashCreateAccountStepElementContentModelContentType,
  SplashFaceIdStepElementContentModelContentType,
} from '@/services/platform/api/model';
import { StepsRow } from './boarding';
import { useAuthStore } from '@/lib/core';

interface SplashProps {
  content: AppSplashScreenContentResponseModel;
  context?: AppContext;
  className?: string;
}

export interface SplashBackdrop {
  /** Video source, when the CMS holds one. */
  video?: string;
  /** Container background. Only ever set when there is no video. */
  style?: CSSProperties;
}

/**
 * Decides what fills the splash backdrop from the two CMS fields that can both be populated.
 *
 * `splashScreenBackground` and `splashScreenVideo` are independent, so a video used to be stacked
 * over a background image that was fetched and painted underneath it. The video now wins outright:
 * the image is not resolved to a URL at all, so nothing requests it and nothing paints it — not even
 * for the few milliseconds before the first frame decodes, which was still visible when the image
 * was merely handed over as the video's poster.
 *
 * The image is therefore the fallback for the no-video case only, which is what the CMS pairing is
 * for. `BackgroundVideo` supplies its own transparent poster, so dropping this one does not bring
 * back the Android play glyph.
 *
 * Kept as a standalone function because `Splash` pulls in MSAL, Capacitor and the auth store, none of
 * which this decision depends on.
 */
export function resolveSplashBackdrop(properties?: AppSplashScreenPropertiesModel): SplashBackdrop {
  const video = properties?.splashScreenVideo?.[0]
    ? getImageUrl(properties.splashScreenVideo[0])
    : undefined;

  if (video) return { video };

  const image = properties?.splashScreenBackground?.[0]
    ? getImageUrl(properties.splashScreenBackground[0])
    : undefined;

  return {
    style: image
      ? { backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : undefined,
  };
}

export function Splash({ content, context, className }: SplashProps) {
  const { instance } = useMsal();
  const locale = useLocaleStore(s => s.locale);
  const {
    accessToken,
    user,
    isAuthenticated,
    hasPermissions,
    isSessionActive,
    isBiometricEnabled,
    setHasPermissions,
    setIsSessionActive,
    setIsWelcomed,
    setIsBiometricEnabled,
  } = useAuthStore();

  const isLogged = !!(accessToken && isAuthenticated && user);
  const [progressDone, setProgressDone] = useState(isLogged);
  const [stepsVisible, setStepsVisible] = useState(isLogged);
  const [isCompleted, setIsCompleted] = useState(isSessionActive);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isPinDone, setIsPinDone] = useState(false);
  const [biometricsAvailable, setBiometricsAvailable] = useState(false);
  const [biometryKind, setBiometryKind] = useState<'face' | 'fingerprint'>('face');
  const [biometricFailed, setBiometricFailed] = useState(false);

  useEffect(() => {
    if (!progressDone) return;
    const id = setTimeout(() => setStepsVisible(true), 500);
    return () => clearTimeout(id);
  }, [progressDone]);

  // Check biometric hardware availability once the user is logged in
  useEffect(() => {
    if (!isLogged || !Capacitor.isNativePlatform()) return;
    BiometricAuth.checkBiometry()
      .then(r => {
        setBiometricsAvailable(r.isAvailable);
        const faceTypes = [BiometryType.faceId, BiometryType.faceAuthentication];
        setBiometryKind(faceTypes.includes(r.biometryType) ? 'face' : 'fingerprint');
      })
      .catch(() => {});
  }, [!!isLogged]);

  // After PIN is done, auto-advance when the Face ID step is not needed
  useEffect(() => {
    if (!isLogged || isCompleted || !isPinDone) return;
    if (!biometricsAvailable || isBiometricEnabled) {
      setIsWelcomed(false);
      setIsCompleted(true);
    }
  }, [isPinDone, biometricsAvailable, isBiometricEnabled]);

  const properties = content?.properties as AppSplashScreenPropertiesModel;

  const splashContext: SplashContext = {
    page: context?.page,
    settings: context?.settings,
    onLogin: () =>
      Capacitor.isNativePlatform()
        ? launchNativeLogin(locale)
        : instance.loginRedirect({
            ...loginRequest,
            prompt: 'login',
            extraQueryParameters: { ui_locales: locale },
          }),
    onCreateAccount: (open: boolean) => setIsCreatingAccount(open),
    biometricVerifyMode: !!(
      isBiometricEnabled &&
      biometricsAvailable &&
      !biometricFailed &&
      !isPinDone &&
      !isCompleted
    ),
    biometryKind,
    onUseBiometric: isBiometricEnabled && biometricsAvailable ? () => setBiometricFailed(false) : undefined,
    onPin: () => setIsPinDone(true),
    onFaceId: () => {
      if (!isBiometricEnabled) setIsBiometricEnabled(true);
      setIsWelcomed(false);
      setIsCompleted(true);
    },
    onSkipFaceId: () => {
      setIsWelcomed(false);
      setIsCompleted(true);
    },
    onFaceIdFallback: () => setBiometricFailed(true),
    onComplete: () => {
      sessionStorage.setItem('session-active', '1');
      setIsSessionActive(true);
      setHasPermissions(true);
      setIsCompleted(true);
    },
  };

  const backdrop = resolveSplashBackdrop(properties);

  let steps = properties?.splashScreenSteps?.items?.map(item => item) as ApiBlockItemModel[] | undefined;

  let current_step: string | undefined =
    SplashOnboardingStep1ElementContentModelContentType?.splashOnboardingStep1;

  let step_fullWidth = false;

  if (!isLogged) {
    if (isCreatingAccount) {
      current_step = SplashCreateAccountStepElementContentModelContentType?.splashCreateAccountStep;
      step_fullWidth = true;
    }
  } else {
    if (!isCompleted) {
      if (!isPinDone) {
        if (isBiometricEnabled && biometricsAvailable && !biometricFailed) {
          current_step = SplashFaceIdStepElementContentModelContentType?.splashFaceIdStep;
        } else {
          current_step = SplashPinStepElementContentModelContentType?.splashPinStep;
        }
      } else if (biometricsAvailable && !isBiometricEnabled) {
        current_step = SplashFaceIdStepElementContentModelContentType?.splashFaceIdStep;
      }
      step_fullWidth = true;
    } else if (!hasPermissions) {
      current_step = SplashOnboardingStep2ElementContentModelContentType?.splashOnboardingStep2;
    } else {
      current_step = SplashWelcomeScreenStepElementContentModelContentType?.splashWelcomeScreenStep;
    }
  }

  steps = steps?.filter(step => {
    const stepContent = step.content as IApiElementModel;
    return stepContent?.contentType == current_step;
  });

  return (
    <div
      className={cn(
        'relative h-screen w-full p-4',
        step_fullWidth && 'p-0',
        'before:absolute before:inset-0 before:bg-black/40 before:content-[""]',
        className
      )}
      style={backdrop.style}
    >
      <BackgroundVideo src={backdrop.video} />

      {!isLogged && (
        <ProgressLoading
          className={cn(
            'absolute top-19 right-0 left-0 z-20 text-center text-white transition-opacity duration-500',
            progressDone ? 'pointer-events-none opacity-0' : 'opacity-100'
          )}
          onComplete={() => setProgressDone(true)}
        />
      )}

      <div
        className={cn(
          'relative z-10 transition-opacity duration-500',
          stepsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        <StepsRow steps={steps} context={splashContext} />
      </div>
    </div>
  );
}
